'use client';

import { c as _c } from "react/compiler-runtime";
import { useRenown, useRenownAuth } from '@powerhousedao/reactor-browser';
import type { ISigner } from 'document-model';
export type AuthState = {
  status: 'authenticated';
  address: string;
  signer: ISigner;
} | {
  status: 'unauthenticated';
} | {
  status: 'pending';
};

/**
 * Source of truth for "is the user signed in and able to sign actions?".
 *
 * The discriminated union forces consumers to handle `pending` explicitly,
 * which fixes the historical bug where `authorized` was checked but the
 * signer hadn't hydrated yet.
 */
export function useAuth() {
  const $ = _c(6);
  const renown = useRenown();
  const auth = useRenownAuth();
  if (auth.status === "loading" || auth.status === "checking") {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        status: "pending"
      };
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  if (auth.status !== "authorized" || !auth.address) {
    let t0;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        status: "unauthenticated"
      };
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  const signer = renown?.signer as ISigner | null | undefined ?? null;
  if (!signer) {
    let t0;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = {
        status: "pending"
      };
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  }
  let t0;
  if ($[3] !== auth.address || $[4] !== signer) {
    t0 = {
      status: "authenticated",
      address: auth.address,
      signer
    };
    $[3] = auth.address;
    $[4] = signer;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return t0;
}

/** Convenience boolean — true only when the user can sign actions right now. */
export function useIsAuthenticated() {
  return useAuth().status === "authenticated";
}